import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { getPersonalTrainerSubscription } from '~/modules/sales/services'
import { Subscription } from '~/modules/sales/types'
import { personalTrainerSubscriptionOffersRoute } from '~/routes/routes'

import { ActiveSubscription } from './components/ActiveSubscription'
import { LicenseExtensions } from './components/LicenseExtensions'
import { NoSubscription } from './components/NoSubscription'
import { Upgrades } from './components/Upgrades'

type SubscriptionsContextData = {
  subscription?: Subscription
  isValid: boolean
  updateSubscription: (payload: Subscription) => void
}

export const subscriptionsContext = createContext<SubscriptionsContextData>(
  {} as SubscriptionsContextData,
)

export function Subscriptions() {
  const [subscription, setSubscription] = useState<Subscription>()
  const [loading, setLoading] = useState<boolean>(true)

  const updateSubscription = useCallback((payload: Subscription) => {
    setSubscription(payload)
  }, [])

  const isValid = useMemo(() => {
    if (!subscription) return false
    const endPeriod = new Date(subscription.termEnd)
    endPeriod.setDate(endPeriod.getDate() + 1)
    return subscription.status === 'active' && endPeriod > new Date()
  }, [subscription])

  useEffect(() => {
    const abortController = new AbortController()
    getPersonalTrainerSubscription(abortController).then((data) => {
      setSubscription(data)
      setLoading(false)
    })
    return () => {
      abortController.abort()
    }
  }, [])

  return (
    <subscriptionsContext.Provider
      value={{
        subscription,
        updateSubscription,
        isValid,
      }}
    >
      <div className='p-5 flex flex-col flex-1 overflow-y-auto'>
        <div className='max-w-[860px] w-full mx-auto flex flex-col gap-4'>
          {loading ? (
            <div
              className='h-10 w-10 border-[8px] rounded-full
            border-r-gpa-blue-50 border-gpa-blue-500 animate-spin
              mx-auto'
            ></div>
          ) : null}
          {!loading && (!subscription || subscription.status === 'canceled') ? (
            <NoSubscription />
          ) : null}

          {loading ? null : <h3 className='font-semibold'>Suas assinaturas</h3>}

          {subscription && !loading ? <ActiveSubscription /> : null}

          {!loading && !isValid ? (
            <div className='flex justify-between items-center'>
              <h1 className='text-2xl font-bold '>
                Veja as opções disponíveis
              </h1>
              <Link
                to={personalTrainerSubscriptionOffersRoute}
                target='_blank'
                rel='noopener noreferrer'
                className='px-3 py-2 bg-gpa-blue-500 rounded-lg font-semibold
                text-white hover:bg-[rgb(48,137,226)] transition-colors
                  duration-200'
              >
                Ir para ofertas
              </Link>
            </div>
          ) : null}

          {subscription && isValid ? (
            <>
              <div className='h-[1px] w-full bg-slate-300 my-4'></div>
              <Upgrades planId={subscription?.planId} />
              <div className='h-[1px] w-full bg-slate-300 my-4'></div>
              <LicenseExtensions
                termStart={subscription.termStart}
                termEnd={subscription.termEnd}
              />
            </>
          ) : null}
        </div>
      </div>
    </subscriptionsContext.Provider>
  )
}
